import { Box, Center, Code, createStyles, Group } from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import {
	apiGetTablePreviewQueryKey,
	useApiGetIntegrationUserCredentials,
	useApiGetTablePreview,
	useApiTestUserCredentialsConnection,
} from '@repo/api-codegen';
import { integrationList } from '@repo/common/constants/integration/integrations';
import { Button } from '@repo/foundations';
import { useQueryClient } from '@tanstack/react-query';
import { isEmpty } from 'lodash-es';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useState } from 'react';
import { useIntegration } from '../../api';
import useSuspenseSecodaEntity from '../../api/hooks/secodaEntity/useSuspenseSecodaEntity';
import { initiateIntegrationOauth } from '../../features/IntegrationOauthConnect';
import { DataDisplayTable } from '../DataDisplayTable';
import { EmptyState } from '../EmptyState';
import FullWidthLoadingSpinner from '../LoadingSpinner';
import { openModal } from '../ModalManager';
import { UserLevelCredentialsModal } from '../Settings/UserLevelCredentialsSettings/UserLevelCredentialsModal';

interface ITableDataPreviewProps {
	tableKey: string;
}

// Number of retries (in addition to the initial request) for the
// table preview query. Previews run long, source-side queries
// retrying more times gives slow tables a better chance to return results.
const TABLE_PREVIEW_RETRY_LIMIT = 8;

// Extended preview response type to include OAuth-related fields
// These fields are returned by the backend when OAuth is required
interface TablePreviewWithOauth {
	columns: string[];
	data: any[];
	column_types?: Record<string, unknown> | null;
	error?: string | null;
	oauth_required?: boolean;
	integration_id?: string | null;
	integration_type?: string | null;
}

const useStyles = createStyles(() => ({
	previewWrapper: {
		width: '100%',
		height: '100%',
		overflowX: 'auto',
	},
}));

function TableDataPreview({ tableKey }: ITableDataPreviewProps) {
	const { classes, theme } = useStyles();
	const queryClient = useQueryClient();

	const [credentialsStatus, setCredentialsStatus] = useState<{
		hasCredentials: boolean;
		isValid: boolean;
		isChecking: boolean;
		error?: string;
	}>({
		hasCredentials: false,
		isValid: false,
		isChecking: false,
	});

	// Get table entity to access integration information
	const { data: tableEntity } = useSuspenseSecodaEntity({
		id: tableKey,
		options: {
			enabled: !!tableKey,
		},
	});

	// Get integration information
	const { data: integration } = useIntegration({
		id: tableEntity?.integration || '',
		options: {
			enabled: !!tableEntity?.integration,
		},
	});

	// Check if user-level credentials are required
	const requiresUserCredentials =
		integration?.query_execute_use_user_level_credentials ?? false;

	// Get user credentials for the integration (always call the hook)
	const { data: userCredentials } = useApiGetIntegrationUserCredentials(
		{
			pathParams: { integrationId: tableEntity?.integration || '' },
		},
		{
			enabled: !!tableEntity?.integration && requiresUserCredentials,
		}
	);

	// Test user credentials connection
	const { mutateAsync: testUserCredentials } =
		useApiTestUserCredentialsConnection({});

	// Handle OAuth connect button click
	const handleOAuthConnect = useCallback(
		async (integrationId: string, integrationType: string) => {
			try {
				await initiateIntegrationOauth(integrationId, integrationType, {
					openInNewTab: true,
				});
				showNotification({
					title: 'Authentication in progress',
					message:
						'Please complete the authentication in the new tab. Once complete, refresh this page to preview data.',
					color: 'blue',
					autoClose: 10000,
				});
			} catch {
				showNotification({
					title: 'Authentication failed',
					message: 'Failed to initiate OAuth authentication. Please try again.',
					color: 'red',
				});
			}
		},
		[]
	);

	// Always call the table preview hook
	const {
		data: tablePreviewRaw,
		error,
		isLoading,
		isError,
	} = useApiGetTablePreview(
		{
			pathParams: { entityId: tableKey },
		},
		{
			// Allow up to TABLE_PREVIEW_RETRY_LIMIT retries before giving up.
			// This targets timeout-style failures for long-running preview queries.
			retry: (failureCount) => failureCount < TABLE_PREVIEW_RETRY_LIMIT,
		}
	);

	// Cast to extended type to access OAuth-related fields
	const tablePreview = tablePreviewRaw as TablePreviewWithOauth | undefined;

	// Check credentials when component mounts or when credentials change
	const checkCredentials = useCallback(async () => {
		if (!requiresUserCredentials || !tableEntity?.integration) {
			setCredentialsStatus({
				hasCredentials: true,
				isValid: true,
				isChecking: false,
			});
			return;
		}

		setCredentialsStatus((prev) => ({ ...prev, isChecking: true }));

		try {
			const result = await testUserCredentials({
				pathParams: { integrationId: tableEntity.integration },
			});

			setCredentialsStatus({
				hasCredentials: true,
				isValid: result.success,
				isChecking: false,
				error: result.success ? undefined : result.error || 'Connection failed',
			});
		} catch (error: any) {
			const isCredentialsNotConfigured =
				error?.message?.includes('not configured') ||
				error?.message?.includes('personal credentials') ||
				error?.status === 400;

			setCredentialsStatus({
				hasCredentials: !isCredentialsNotConfigured,
				isValid: false,
				isChecking: false,
				error: isCredentialsNotConfigured
					? 'User credentials not configured'
					: error?.message || 'Connection failed',
			});
		}
	}, [requiresUserCredentials, tableEntity?.integration, testUserCredentials]);

	// Check credentials when dependencies change
	useEffect(() => {
		if (integration !== undefined) {
			checkCredentials();
		}
	}, [integration, userCredentials, checkCredentials]);

	const handleConfigureCredentials = useCallback(() => {
		if (!integration) return;

		const spec = integrationList.find((spec) => spec.type === integration.type);
		if (!spec?.userLevelCredentialsFields?.length) {
			showNotification({
				title: 'Not supported',
				message: 'This integration does not support user-level credentials',
				color: 'orange',
			});
			return;
		}

		openModal({
			title: `Configure credentials for ${integration.name}`,
			children: (
				<UserLevelCredentialsModal
					integration={integration}
					onCredentialsSaved={() => {
						// Refresh credentials status
						checkCredentials();
						// Invalidate the table preview query so it refetches with new credentials
						queryClient.invalidateQueries({
							queryKey: apiGetTablePreviewQueryKey({
								pathParams: { entityId: tableKey },
							}),
						});
					}}
				/>
			),
			size: 'lg',
		});
	}, [integration, checkCredentials, queryClient, tableKey]);

	// Handle different UI states based on credentials status
	if (
		requiresUserCredentials &&
		(!credentialsStatus.hasCredentials || !credentialsStatus.isValid)
	) {
		if (credentialsStatus.isChecking) {
			return (
				<Box my={20}>
					<FullWidthLoadingSpinner />
				</Box>
			);
		}

		return (
			<Center h="100%">
				<EmptyState
					iconName="key"
					size="lg"
					title="User credentials required"
					description={
						credentialsStatus.hasCredentials
							? `Your credentials are invalid: ${credentialsStatus.error}`
							: 'This integration requires you to configure your personal credentials to preview data.'
					}
					withActions={
						<Group spacing="sm">
							<Button variant="primary" onClick={handleConfigureCredentials}>
								{credentialsStatus.hasCredentials
									? 'Update credentials'
									: 'Configure credentials'}
							</Button>
							<Button
								variant="tertiary"
								onClick={checkCredentials}
								loading={credentialsStatus.isChecking}
							>
								Test connection
							</Button>
						</Group>
					}
					includeGoBack={false}
				/>
			</Center>
		);
	}

	// Handle OAuth authentication required error
	if (
		tablePreview?.oauth_required &&
		tablePreview?.integration_id &&
		tablePreview?.integration_type
	) {
		return (
			<Center h="100%">
				<EmptyState
					iconName="lock"
					size="lg"
					title="Authentication required"
					description="Connect your account to run queries in Secoda"
					withActions={
						<Button
							variant="default"
							onClick={() =>
								handleOAuthConnect(
									tablePreview.integration_id!,
									tablePreview.integration_type!
								)
							}
							rightIconName="arrowUpRight"
						>
							Connect
						</Button>
					}
					includeGoBack={false}
				/>
			</Center>
		);
	}

	// Handle table preview errors
	if (isError || tablePreview?.error) {
		return (
			<Center h="100%">
				<EmptyState
					iconName="serverBolt"
					size="lg"
					title="Preview error"
					description="The following error occurred fetching the data preview."
					withActions={
						<Code
							block
							data-testid="table-data-preview-error-code-block-81de3d78b"
						>
							{error?.detail ?? tablePreview?.error ?? 'Unknown error'}
						</Code>
					}
					includeGoBack={false}
				/>
			</Center>
		);
	}

	// Handle loading state
	if (isLoading || !tablePreview) {
		return (
			<Box my={20}>
				<FullWidthLoadingSpinner />
			</Box>
		);
	}

	// Handle empty data
	if (isEmpty(tablePreview.data)) {
		return (
			<Center h="100%" my={theme.spacing.xl} w="100%">
				<EmptyState
					size="lg"
					iconName="tableRow"
					title="No data"
					description="This table has no data. Ensure Secoda is granted permissions to view the data."
					includeGoBack={false}
				/>
			</Center>
		);
	}

	// Handle no columns
	if (tablePreview.columns.length === 0) {
		return (
			<Center h="100%" my={theme.spacing.xl} w="100%">
				<EmptyState
					size="lg"
					iconName="tableRow"
					title="No columns"
					description="This table has no columns that can be preview"
					includeGoBack={false}
				/>
			</Center>
		);
	}

	// Render successful preview
	return (
		<Box className={classes.previewWrapper}>
			<DataDisplayTable
				width={`${theme.other.space[80] * tablePreview.columns.length}px`}
				height="calc(100vh - 300px)"
				results={[...[tablePreview.columns], ...tablePreview.data]}
			/>
		</Box>
	);
}

const TableDataPreviewObserver = observer(TableDataPreview);
export default TableDataPreviewObserver;
